import Seo from '@/components/Seo';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter } from 'lucide-react';
import BookCard from '@/components/BookCard';
import ComingSoonCard from '@/components/ComingSoonCard';
import { getBooks } from '@/lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BookstorePage = () => {
  const [books, setBooks] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');

  const genres = ['all', 'Literary Fiction', 'Historical Fiction', 'Poetry'];

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const data = await getBooks();
        const available = data.filter((b) => !b.coming_soon);
        const soon = data.filter((b) => b.coming_soon);
        setBooks(available);
        setComingSoon(soon);
        setFilteredBooks(available);
      } catch (error) {
        console.error('Error fetching books:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBooks();
  }, []);

  useEffect(() => {
    let result = books;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (book) =>
          book.title.toLowerCase().includes(query) ||
          book.author.toLowerCase().includes(query) ||
          book.genre.toLowerCase().includes(query)
      );
    }
    
    if (selectedGenre !== 'all') {
      result = result.filter((book) => book.genre === selectedGenre);
    }
    
    setFilteredBooks(result);
  }, [searchQuery, selectedGenre, books]);

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="bookstore-page">
      <Seo title="Books by Kavi Ocktiiv" description="Shop novels by Kavi Ocktiiv in paperback and hardcover — The Omission of Billy Seldom, Shadows in the Magnolias, and upcoming titles from Ink & Innovation Printworks." />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="overline text-gold mb-4">Our Collection</p>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Bookstore
          </h1>
          <p className="text-white/60 max-w-2xl">
            Browse our carefully curated collection of literary works. Each title has been selected for its 
            artistic merit, compelling narrative, and ability to illuminate the human experience.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 mb-12"
        >
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, author, or genre..."
              className="input-underline pl-8"
              data-testid="search-input"
            />
          </div>

          {/* Genre Filter */}
          <Select value={selectedGenre} onValueChange={setSelectedGenre}>
            <SelectTrigger className="w-full sm:w-[200px] bg-transparent border-white/20 rounded-none" data-testid="genre-filter">
              <Filter size={16} className="mr-2 text-white/40" />
              <SelectValue placeholder="Genre" />
            </SelectTrigger>
            <SelectContent className="bg-[#101010] border-white/10">
              {genres.map((genre) => (
                <SelectItem key={genre} value={genre} className="text-white/80 focus:bg-white/10 focus:text-white">
                  {genre === 'all' ? 'All Genres' : genre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Results Count */}
        <p className="text-white/40 text-sm mb-8 mono">
          {filteredBooks.length} {filteredBooks.length === 1 ? 'title' : 'titles'} found
        </p>

        {/* Books Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-4">
                <div className="aspect-[2/3] bg-white/5 img-loading" />
                <div className="h-4 bg-white/5 w-1/3 img-loading" />
                <div className="h-6 bg-white/5 w-2/3 img-loading" />
              </div>
            ))}
          </div>
        ) : filteredBooks.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-white/40 text-lg">No books found matching your criteria.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedGenre('all');
              }}
              className="text-gold hover:text-gold/80 transition-colors mt-4 text-sm uppercase tracking-widest"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredBooks.map((book, index) => (
              <BookCard key={book.id} book={book} index={index} />
            ))}
          </div>
        )}

        {/* Coming Soon Section */}
        {comingSoon.length > 0 && (
          <section className="mt-24 pt-16 border-t border-white/10" data-testid="coming-soon-section">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <p className="overline text-gold mb-4">On the Horizon</p>
              <h2 className="text-3xl lg:text-4xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Coming Soon
              </h2>
              <p className="text-white/60 max-w-2xl">
                New titles from Ink and Innovation are on the way. Sign up to be notified the moment they release.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {comingSoon.map((book, index) => (
                <ComingSoonCard key={book.id} book={book} index={index} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BookstorePage;
